import { createContext, useContext } from 'react';
import type { Path } from 'react-router-dom';

export type HotelScope = {
  clientId: string;
  clientName: string;
  hasMultipleClients: boolean;
  stateKey?: string;
};
export const HotelScopeContext = createContext<HotelScope | null>(null);
export const useHotelScope = () => useContext(HotelScopeContext);

export function clientFromPath(pathname: string): string | null {
  return /^\/clients\/([1-9]\d*)(?:\/|$)/.exec(pathname)?.[1] ?? null;
}

export function hotelPath(
  clientId: string | null | undefined,
  path: string,
): string {
  if (
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.startsWith('/clients/') ||
    path === '/select-client'
  )
    return path;
  if (!clientId) return '/select-client';
  return `/clients/${clientId}${path === '/' ? '/dashboard' : path}`;
}

/** Keep reusable pages, but never carry a company's record or booking draft across. */
export function companySwitchDestination(
  clientId: string,
  location: Path,
): Path {
  const path = location.pathname.replace(/^\/clients\/[1-9]\d*/, '');
  if (/^\/(roster|trips|contact|playground|requests(?:\/new)?)\/?$/.test(path))
    return {
      pathname: hotelPath(clientId, path),
      search: location.search,
      hash: location.hash,
    };

  // Detail/edit routes belong to the old company. Booking steps depend on
  // discarded search, offer, and guest selections, so restart at the dashboard.
  const destination = /^\/requests\//.test(path)
    ? '/requests'
    : /^\/booking-confirmation(?:\/|$)/.test(path)
      ? '/trips'
      : '/dashboard';
  return { pathname: hotelPath(clientId, destination), search: '', hash: '' };
}
